import { loadPdf, renderPage } from './pdf';

/**
 * Printing a document the app is storing.
 *
 * The first version put the file in a hidden iframe and asked the browser's
 * viewer to print it. That depends on the browser having a PDF plugin, on the
 * plugin honouring a print request from script, and on the page's
 * content-security policy allowing the embed — three things to be at the mercy
 * of, and the first of them failed.
 *
 * So the pages are drawn here, with the same renderer the viewer uses, into a
 * print-only container. What goes to the printer is then plain HTML and
 * images, which every browser can print because it is the only thing they all
 * agree on.
 *
 * The trade, stated plainly: a page printed this way is an image of the page
 * rather than its text. At 150 dpi a form or a letter is indistinguishable in
 * the hand; text will not be selectable in a print-to-PDF, and very fine hair
 * lines can soften. In exchange it works.
 */

/** Roughly 150 dpi across a sheet of Letter — enough for anything printed. */
const PRINT_WIDTH = 1275;

const HOST_ID = 'td-print-host';

function host(): HTMLElement {
  const existing = document.getElementById(HOST_ID);
  if (existing) return existing;

  const node = document.createElement('div');
  node.id = HOST_ID;
  /*
   * Present in the document but never on screen: `display: none` would mean
   * the browser skips laying it out, and something never laid out has nothing
   * to print.
   */
  node.className = 'td-print-only';
  document.body.appendChild(node);

  return node;
}

function clear(): void {
  document.getElementById(HOST_ID)?.replaceChildren();
}

/**
 * One thing on its own sheet, turned the way it is.
 *
 * The orientation is measured rather than chosen. A picture wider than it is
 * tall was printing as a band across the middle of a portrait page, and the
 * print dialog offered no way to say otherwise — Chrome disables the
 * orientation control as soon as a stylesheet declares a page size, which
 * ours did. Naming the orientation per sheet is what lets a batch of mixed
 * documents go out as one job with each page the right way round.
 */
function sheet(child: HTMLElement, landscape: boolean): HTMLElement {
  const page = document.createElement('div');
  page.className = landscape ? 'td-print-page td-landscape' : 'td-print-page';
  page.appendChild(child);
  return page;
}

/** Wider than it is tall. Square counts as portrait; it prints the same. */
function isLandscape(width: number, height: number): boolean {
  return width > height;
}

export interface Printable {
  url: string;
  contentType: string;
  /** For a Word file: the HTML the server converted it to. */
  html?: string;
}

/**
 * Prints one or several documents as a single job.
 *
 * A single job, which the iframe approach could never manage — four PDFs
 * meant four dialogs. Everything is drawn into one container and printed
 * once, with a page break between documents.
 */
export async function printDocuments(items: Printable[]): Promise<void> {
  const container = host();
  container.replaceChildren();

  for (const item of items) {
    if (item.contentType === 'application/pdf') {
      const pdf = await loadPdf(item.url);

      for (let n = 1; n <= pdf.numPages; n++) {
        // A PDF carries its own page size, so each page is asked individually
        // — a deck of wide slides with one portrait cover prints correctly.
        const { canvas, width, height } = await renderPage(pdf, n, PRINT_WIDTH);
        canvas.className = 'td-print-image';
        container.appendChild(sheet(canvas, isLandscape(width, height)));
      }
      continue;
    }

    if (item.contentType.startsWith('image/')) {
      const image = document.createElement('img');
      image.src = item.url;
      image.className = 'td-print-image';

      // The dialog must not open before the picture has arrived — and the
      // shape of the picture is not known until it has.
      await new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });

      container.appendChild(sheet(image, isLandscape(image.naturalWidth, image.naturalHeight)));
      continue;
    }

    if (item.html) {
      const article = document.createElement('article');
      // Already sanitised on the server, before it was stored.
      article.innerHTML = item.html;
      article.className = 'document-html';

      // Converted Word is a column of text with no page size of its own, and
      // text reads down a portrait page.
      container.appendChild(sheet(article, false));
    }
  }

  if (!container.firstChild) return;

  /*
   * A frame for the browser to lay the container out before the dialog
   * freezes it, and a clear-up afterwards — the dialog is modal and gives no
   * event to wait on, so this runs once it has been dismissed and control
   * comes back.
   */
  await new Promise((resolve) => window.setTimeout(resolve, 80));

  window.print();
  window.setTimeout(clear, 1000);
}
